import React from 'react';
import { motion } from 'framer-motion';
import {
  Eye,
  ShieldCheck,
  Repeat,
  ArrowRight,
  CheckCircle2,
  Calendar,
  Sparkles,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { fadeUp, displaySection, kicker, sectionWrap, sectionInner } from './shared';

/* ------------------------------------------------------------------ */
/*  Inline mini-infographics — each maps a "silent question" to its    */
/*  outcome. Built as crisp inline SVG so they scale without artifacts.*/
/* ------------------------------------------------------------------ */

/** Discoverability — ascending bar chart with a callout pin. */
const DiscoverabilityViz = () => (
  <svg viewBox="0 0 280 90" className="w-full h-auto" aria-hidden="true">
    <defs>
      <linearGradient id="disc-bar" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stopColor="hsl(212 92% 52%)" stopOpacity="0.95" />
        <stop offset="100%" stopColor="hsl(205 85% 45%)" stopOpacity="0.55" />
      </linearGradient>
    </defs>
    {[10, 18, 14, 28, 36, 52, 70].map((h, i) => (
      <rect
        key={i}
        x={6 + i * 38}
        y={82 - h}
        width="22"
        height={h}
        rx="3"
        fill={i === 6 ? 'url(#disc-bar)' : 'hsl(214 20% 86%)'}
      />
    ))}
    <text x={6 + 6 * 38 + 11} y={82 - 70 - 8} textAnchor="middle" fontSize="9" fontFamily="ui-monospace,monospace" fill="hsl(212 86% 36%)" fontWeight="600">
      +320%
    </text>
    <line x1="0" y1="82" x2="280" y2="82" stroke="hsl(214 20% 88%)" strokeWidth="1" />
  </svg>
);

/** Voice match — a soft sine waveform with a "match" needle. */
const VoiceMatchViz = () => (
  <svg viewBox="0 0 280 90" className="w-full h-auto" aria-hidden="true">
    <defs>
      <linearGradient id="voice-line" x1="0" x2="1">
        <stop offset="0%" stopColor="hsl(212 92% 52%)" />
        <stop offset="100%" stopColor="hsl(205 85% 45%)" />
      </linearGradient>
    </defs>
    {[0.15, 0.4, 0.2, 0.55, 0.3, 0.7, 0.25, 0.5, 0.3].map((amp, i) => (
      <line
        key={i}
        x1={20 + i * 30}
        y1={45 - amp * 28}
        x2={20 + i * 30}
        y2={45 + amp * 28}
        stroke="hsl(214 20% 80%)"
        strokeWidth="2"
        strokeLinecap="round"
      />
    ))}
    <path
      d="M 8 45 Q 35 25, 60 45 T 110 45 T 160 45 T 210 45 T 270 45"
      fill="none"
      stroke="url(#voice-line)"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
    {/* needle */}
    <circle cx="140" cy="45" r="4" fill="hsl(212 86% 36%)" />
    <circle cx="140" cy="45" r="9" fill="hsl(212 86% 36%)" fillOpacity="0.18" />
    <text x="140" y="80" textAnchor="middle" fontSize="9" fontFamily="ui-monospace,monospace" fill="hsl(215 16% 40%)" fontWeight="600">
      VOICE MATCH · 94%
    </text>
  </svg>
);

/** Cadence — 4-week dot calendar showing a weekly rhythm. */
const CadenceViz = () => {
  const days = Array.from({ length: 28 });
  const lit = new Set([2, 9, 16, 23]); // weekly post cadence
  return (
    <svg viewBox="0 0 280 90" className="w-full h-auto" aria-hidden="true">
      {days.map((_, i) => {
        const col = i % 7;
        const row = Math.floor(i / 7);
        const cx = 14 + col * 38;
        const cy = 18 + row * 18;
        const isLit = lit.has(i);
        return (
          <g key={i}>
            <circle
              cx={cx}
              cy={cy}
              r={isLit ? 6 : 3.5}
              fill={isLit ? 'hsl(212 86% 36%)' : 'hsl(214 20% 86%)'}
            />
            {isLit && (
              <circle cx={cx} cy={cy} r="10" fill="hsl(212 86% 36%)" fillOpacity="0.14" />
            )}
          </g>
        );
      })}
      <text x="14" y="86" fontSize="8" fontFamily="ui-monospace,monospace" fill="hsl(215 16% 50%)" letterSpacing="2">
        M  T  W  T  F  S  S
      </text>
    </svg>
  );
};

/* ------------------------------------------------------------------ */
/*  Cards data                                                         */
/* ------------------------------------------------------------------ */

const PROMISES = [
  {
    id: 'discover',
    silent: 'Who are you?',
    Icon: Eye,
    title: 'Be easier to discover',
    desc: 'Show up clearly for the roles, industries, and opportunities you actually want to attract.',
    bullets: [
      'Target-role headline + About rewrite',
      'Recruiter & buyer keyword coverage (without stuffing)',
      'Skill section ranked for your role (peer benchmarking on Enterprise*)',
    ],
    stat: '3.2\u00d7',
    statLabel: 'more profile views in 30 days (median)',
    Viz: DiscoverabilityViz,
    tint: 'hsl(212 100% 97%)',
  },
  {
    id: 'trust',
    silent: 'Why trust you?',
    Icon: ShieldCheck,
    title: 'Build trust faster',
    desc: 'Read like a leader, not a resume. Current, credible and unmistakably you.',
    bullets: [
      'Proof points your career actually earned',
      'Voice-match learns your last 10 posts',
      'Every line is yours to edit',
    ],
    stat: '94%',
    statLabel: 'voice-match accuracy on first pass',
    Viz: VoiceMatchViz,
    tint: 'hsl(150 60% 96%)',
  },
  {
    id: 'cadence',
    silent: 'Why now?',
    Icon: Repeat,
    title: 'Stay visible consistently',
    desc: 'A weekly rhythm without becoming a content creator. Calm cadence, real proof.',
    bullets: [
      '10 voice-matched drafts per month',
      'Researched posts + PoVs + prompt drafts',
      '6 premium carousels rendered natively',
    ],
    stat: '10',
    statLabel: 'posts / month \u2014 calm, not loud',
    Viz: CadenceViz,
    tint: 'hsl(38 92% 96%)',
  },
];

/* ------------------------------------------------------------------ */
/*  Section                                                            */
/* ------------------------------------------------------------------ */

export const PromiseSection = () => (
  <section className={sectionWrap} aria-label="The promise" id="promise">
    <div className={sectionInner}>
      {/* Header */}
      <motion.div {...fadeUp} className="mb-12 max-w-3xl">
        <p className={kicker}>The promise</p>
        <h2 className={`${displaySection} mt-4 text-4xl sm:text-5xl lg:text-6xl`}>
          Easier to discover.<br />
          Easier to trust.{' '}
          <span className="text-primary">Easier to choose.</span>
        </h2>
        <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
          Your LinkedIn is the first place a recruiter, client, investor, or peer
          checks before deciding whether you&rsquo;re worth a conversation. We
          quietly answer the three silent questions every visitor asks &mdash;
          before they ever message you.
        </p>
      </motion.div>

      {/* Silent-questions flow diagram (infographic) */}
      <motion.div
        {...fadeUp}
        transition={{ ...fadeUp.transition, delay: 0.08 }}
        className="mb-14"
        data-testid="silent-questions-flow"
      >
        <Card className="rounded-3xl border border-border/70 bg-card/85 backdrop-blur overflow-hidden">
          <CardContent className="p-6 sm:p-8">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] gap-5 md:gap-3 items-center">
              {/* Question column */}
              <Step
                label="Silent question"
                tone="muted"
                items={['\u201cWho are you?\u201d', '\u201cWhy should I trust you?\u201d', '\u201cWhy now?\u201d']}
              />
              <Connector />
              <Step
                label="We answer with"
                tone="brand"
                items={[
                  'Sharpened profile + positioning',
                  'Voice-matched proof points',
                  'A weekly publishing rhythm',
                ]}
              />
              <Connector />
              <Step
                label="What changes"
                tone="solid"
                items={[
                  '+ Easier to discover',
                  '+ Easier to trust',
                  '+ Easier to choose',
                ]}
              />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Developed promise cards */}
      <div className="grid lg:grid-cols-3 gap-5 sm:gap-6" data-testid="promise-grid">
        {PROMISES.map(({ id, silent, Icon, title, desc, bullets, stat, statLabel, Viz, tint }) => (
          <motion.div key={id} {...fadeUp}>
            <Card
              className="group h-full rounded-3xl border border-border/70 bg-card/85 backdrop-blur hover:-translate-y-1 hover:shadow-[0_30px_70px_-40px_hsl(220_40%_2%/0.35)] transition-[transform,box-shadow] duration-300 overflow-hidden"
              data-testid={`promise-card-${id}`}
            >
              <CardContent className="p-7 sm:p-8 flex flex-col h-full">
                {/* silent question pill + icon */}
                <div className="flex items-center justify-between gap-3">
                  <Badge
                    variant="secondary"
                    className="rounded-full px-3 py-1 text-[11px] font-medium tracking-tight bg-background/70 border border-border/70"
                  >
                    {silent}
                  </Badge>
                  <div
                    className="w-10 h-10 rounded-xl grid place-items-center text-primary border border-primary/15"
                    style={{ background: tint }}
                  >
                    <Icon size={18} aria-hidden="true" />
                  </div>
                </div>

                {/* title + desc */}
                <h3 className="mt-6 text-xl sm:text-[22px] font-semibold tracking-[-0.02em]">{title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{desc}</p>

                {/* infographic */}
                <div
                  className="mt-6 rounded-2xl border border-border/60 p-4"
                  style={{ background: tint }}
                  aria-hidden="true"
                >
                  <Viz />
                </div>

                {/* sub-bullets */}
                <ul className="mt-5 space-y-2.5">
                  {bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-[13.5px]">
                      <CheckCircle2 size={14} className="mt-0.5 text-primary shrink-0" aria-hidden="true" />
                      <span className="text-foreground/90 leading-snug">{b}</span>
                    </li>
                  ))}
                </ul>

                {/* stat */}
                <div className="mt-7 pt-5 border-t border-dashed border-border/70">
                  <p className="font-display font-semibold text-4xl tracking-[-0.045em] tabular-nums text-foreground">
                    {stat}
                  </p>
                  <p className="mt-1 text-[11px] font-mono-edge tracking-[0.18em] uppercase text-muted-foreground">
                    {statLabel}
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Closing outcome strip */}
      <motion.div
        {...fadeUp}
        transition={{ ...fadeUp.transition, delay: 0.1 }}
        className="mt-12"
        data-testid="promise-outcome-strip"
      >
        <Card className="rounded-2xl border border-border/70 bg-[hsl(var(--ep-surface-2,215_25%_97%))]/70 backdrop-blur">
          <CardContent className="p-6 sm:p-7 flex flex-wrap items-center gap-5 sm:gap-8 justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-card border border-border/70 grid place-items-center text-primary shrink-0">
                <Sparkles size={17} aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground">
                  What that adds up to
                </p>
                <p className="text-[15px] sm:text-base font-medium text-foreground tracking-[-0.01em]">
                  A LinkedIn that quietly does the warm-up &mdash; before the meeting.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-6 sm:gap-8 flex-wrap">
              {[
                ['3.2\u00d7', 'profile views'],
                ['94%', 'voice match'],
                ['10', 'posts / month'],
                ['0', 'burned weekends'],
              ].map(([k, l]) => (
                <div key={l} className="flex flex-col">
                  <span className="font-display font-semibold text-2xl sm:text-[28px] tracking-[-0.045em] tabular-nums">{k}</span>
                  <span className="text-[10px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground">{l}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ */
/*  Sub-components for the flow diagram                                */
/* ------------------------------------------------------------------ */

const Step = ({ label, items, tone }) => {
  const itemClass =
    tone === 'brand'
      ? 'bg-[hsl(var(--ep-accent-soft,212_100%_96%))] text-primary border-primary/20'
      : tone === 'solid'
      ? 'bg-foreground text-background border-foreground'
      : 'bg-card text-foreground border-border/70';

  return (
    <div className="min-w-0">
      <p className="text-[10px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground mb-3">
        {label}
      </p>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li
            key={i}
            className={`rounded-xl border px-3.5 py-2 text-[13px] leading-snug font-medium ${itemClass}`}
          >
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
};

const Connector = () => (
  <div className="hidden md:flex items-center justify-center pt-6">
    <div className="flex flex-col gap-3" aria-hidden="true">
      <ArrowRight size={16} className="text-muted-foreground/60" />
      <ArrowRight size={16} className="text-muted-foreground/60" />
      <ArrowRight size={16} className="text-muted-foreground/60" />
    </div>
  </div>
);
